// Einstieg des Clients. Zwei Aufgaben, in dieser Reihenfolge:
//
//  1. Sitzung: `GET /api/ich` entscheidet beim Laden, was zu sehen ist – die Anmeldeseite oder das
//     Spiel. Ohne Sitzung gibt es keine Gastansicht. Wird das Konto woanders angemeldet, kommt
//     über die Verbindung `kicked`, und über allem steht der Hinweis mit „Neu anmelden“
//     (docs/architektur-paket-2.md, Abschnitt „Client (auth/)“, ADR 0020). Der Zustandsautomat
//     dazu liegt in auth/zustand.ts und ist ohne DOM geprüft.
//  2. Spiel: Verbindung aufbauen und auf `hello` warten. Erst dann entsteht die Welt, und zwar aus
//     `hello.params`, geprüft gegen `hello.worldId` (game/welt.ts, ADR 0027). Danach Spielzustand,
//     Szene, Tiere, Licht, Eingabe, HUD, Schilder und die Spielschleife. Die Simulation läuft in
//     festen Schritten (TICK_RATE) über einen Akkumulator, das Bild so oft wie der Browser will;
//     Kamera und Tiere interpolieren zwischen den zwei letzten Ticks. Der Spielzustand liegt in
//     einer Variablen und wird den Ansichten übergeben; die Three.js-Objekte halten keinen Zustand
//     (docs/architektur.md, Abschnitt client). Die Einhängung der Teile folgt ADR 0012 (HUD),
//     ADR 0013 (Uhr und Licht), ADR 0014 (Tiere) und ADR 0015 (Reihenfolge, Handlung, Test-Haken).
//
// Seit task-013 ist der Server Herr über Besitz, Tiere, Tor und Inventar (ADR 0027): Tiere und
// Tore kommen aus `farm`-Nachrichten, das Inventar aus `inventory`; die eigene Simulation bewegt
// keine Tiere mehr (`animals: false`) und führt keine Handlung aus. Die Taste der Handlung im Blick
// wird eine `interact`-Nachricht, und die eigene Position geht als `move` an den Server, der daran
// die Nähe misst.
//
// Seit task-015 meldet der Client, ob sein Tab zu sehen ist (`visibility`): versteckt heißt beim
// Server sofort abwesend. Zurück holt einen die nächste Eingabe, die ohnehin hinausgeht. Oben links
// stehen Tageszeit und Hoftag; der Stand der Hof-Uhr kommt aus `hello`, `clock` und dem eigenen Hof.
//
// Seit task-014 (ADR 0031) geht die eigene Kiste nach „Öffnen [E]“ und der Zusage des Servers als
// Ansicht auf (game/kiste.ts, hud/kistenansicht.ts): Die Maus wird frei, ein Klick auf einen Stapel
// wird `chestMove`, Escape oder E schließt. Die Äpfel am Baum setzt der Client als Blöcke in die
// Krone, so viele, wie der Hof des Servers meldet (game/baum.ts).
//
// Seit task-016 (ADR 0032) sieht man die anderen: Mitspieler kommen aus `hello.players`,
// `playerJoined` und `playerLeft`, bewegt werden sie über `players` (game/remotePlayers.ts) und
// gezeichnet als Figuren mit Namensschild und Sprechblase (render/mitspieler.ts). Tab zeigt, solange
// gehalten, wer da ist; T öffnet das Satz-Menü. Die eigene Position geht im Takt der Simulation hinaus,
// damit die anderen einen gleichmäßigen Gang sehen, und die Höfe eines Takts kommen gebündelt (`farms`).

import {
  PLAYER,
  PROTOCOL_VERSION,
  TICK_SECONDS,
  createGameState,
  dayFraction,
  generateWorld,
  isSolid,
  stepSimulation,
  type Action,
  type ActionResultMessage,
  type AvatarColors,
  type ChestSide,
  type FarmState,
  type GameState,
  type GameWorld,
  type HelloMessage,
  type Intent,
  type InteractionContext,
  type ItemStack,
  type Kistenfach,
  type PhraseIndex,
  type ServerMessage,
  type Vec3,
  type World,
} from '@bauernhof/shared';
import {
  START,
  browserHol,
  holeIch,
  naechsterZustand,
  sendeAbmelden,
  sendeAvatar,
  zeigeAnmeldeseite,
  zeigeKickHinweis,
  type Ereignis,
  type IchDaten,
  type Zustand,
} from './auth/index.js';
import { chunksDerAepfel, setzeAepfel } from './game/baum.js';
import { createPositionsmelder, ruecksetzungFuer, simZeitMs } from './game/bewegung.js';
import { createHofAnsicht } from './game/hoefe.js';
import { createInteraktion, handlungImBlick } from './game/interaktion.js';
import { createKiste } from './game/kiste.js';
import { createRemotePlayers, type MitspielerStand } from './game/remotePlayers.js';
import { createSichtbarkeitsmelder } from './game/sichtbarkeit.js';
import { chunksUmBlock, setzeTor } from './game/tor.js';
import { NEU_GELADEN_SCHLUESSEL, weltEntscheidung } from './game/welt.js';
import { anwesenheitsZeilen, createAnwesenheitsliste, type AnwesenheitsZeile } from './hud/anwesenheitsliste.js';
import { createFpsDisplay } from './hud/fps.js';
import { createHud } from './hud/hud.js';
import { createKistenansicht } from './hud/kistenansicht.js';
import { createMenu, type Menu } from './hud/menu.js';
import { createSatzmenue } from './hud/satzmenue.js';
import { createSpiegel } from './hud/spiegel.js';
import { buildInput } from './input/index.js';
import { createWorldClock, worldClockUrl, type WorldClock } from './net/clock.js';
import { verarbeiteNachricht, type NachrichtenHandler } from './net/nachrichten.js';
import { createAnimalViews } from './render/animals.js';
import { createArme } from './render/arms.js';
import { createAvatarModel } from './render/avatarModel.js';
import { createSprechblase, createSprechblasen } from './render/bubbles.js';
import { applyPose, copyPlayerState, interpolatePose } from './render/camera.js';
import { applyDaylight, createDaylightTargets } from './render/daylight.js';
import { createGang } from './render/gang.js';
import { createMitspielerAnsicht } from './render/mitspieler.js';
import { createSchilder } from './render/schilder.js';
import { createScene } from './render/scene.js';
import { schulterPose } from './render/schulterkamera.js';

/** Bleibt der Browser lange weg (Tab im Hintergrund), werden höchstens so viele Ticks nachgeholt. */
const MAX_TICKS_PER_FRAME = 5;
/** So viele Antworten des Servers hält der Test-Haken zum Nachsehen vor. */
const TEST_ANTWORTEN = 10;
/** Der Satz, wenn eine Handlung gar nicht erst hinausging. */
const OHNE_VERBINDUNG = 'Keine Verbindung zum Hof.';

/** Welche Kamera zeigt: die Ego-Sicht oder die Schulterkamera hinter dem eigenen Avatar. */
type Sicht = 'ego' | 'schulter';

/** Ein laufendes Spiel; mehr braucht der Rahmen von ihm nicht zu wissen. */
interface Spiel {
  /** Hält Schleife, Verbindung und Eingabe an – nach einem Kick spielt niemand weiter. */
  halteAn(): void;
}

/** Das Spiel mit gebauter Welt; es entsteht mit dem ersten `hello`. */
interface LaufendesSpiel {
  /** Eine Nachricht des Servers, nach dem ersten `hello`. */
  nimm(msg: ServerMessage): void;
  halteAn(): void;
}

const canvas = holeCanvas();
const hol = browserHol();

let zustand: Zustand = START;
let spiel: Spiel | null = null;
let sitzungspruefungLaeuft = false;

void beginne();

/** Beim Laden: Sitzung erfragen und den Zustandsautomaten damit füttern. */
async function beginne(): Promise<void> {
  const antwort = await holeIch(hol);
  if (antwort.ok) melde({ art: 'sitzungDa', ich: antwort.wert });
  else melde({ art: 'sitzungFehlt', grund: antwort.grund ?? 'keine' });
}

/** Ein Ereignis an den Zustandsautomaten geben und die Ansicht nachziehen. */
function melde(ereignis: Ereignis): void {
  const neu = naechsterZustand(zustand, ereignis);
  if (neu === zustand) return;
  const vorher = zustand;
  zustand = neu;
  zeige(vorher, neu);
}

/**
 * Bringt die Ansicht auf den neuen Zustand. Szene, Verbindung und Spielzustand werden je
 * Seitenaufruf genau einmal aufgebaut: Jeder Wechsel zwischen Anmeldeseite und Spiel lädt die
 * Seite neu (ADR 0020). Das ist der kürzeste Weg, der nichts halb Abgebautes hinterlässt – und
 * nach dem Anmelden trägt der Neuaufbau das frische Sitzungscookie von Anfang an mit.
 */
function zeige(vorher: Zustand, jetzt: Zustand): void {
  if (vorher.ansicht !== 'prueft' && (jetzt.ansicht === 'anmeldung' || jetzt.ansicht === 'spiel')) {
    window.location.reload();
    return;
  }

  switch (jetzt.ansicht) {
    case 'prueft':
      return;

    case 'anmeldung':
      canvas.hidden = true;
      zeigeAnmeldeseite(document.body, {
        hol,
        hinweis: jetzt.hinweis,
        beiErfolg: (ich) => melde({ art: 'angemeldet', ich }),
      });
      return;

    case 'spiel':
      if (jetzt.ich === null) return;
      spiel = starteSpiel(jetzt.ich);
      return;

    case 'kick':
      spiel?.halteAn();
      spiel = null;
      // Der Hinweis bleibt bis zum Neuladen stehen; „Neu anmelden“ lädt die Seite neu.
      zeigeKickHinweis(document.body, {
        name: jetzt.ich?.name ?? null,
        beiNeuAnmelden: () => melde({ art: 'zurueckZurAnmeldung' }),
      });
      return;
  }
}

/**
 * Fragt nach einem Verbindungsabbruch nach, ob die Sitzung noch gilt. Der Browser verrät nicht,
 * warum ein WebSocket-Handshake scheiterte; ein 401 auf `GET /api/ich` sagt es dagegen deutlich.
 * Nur ein echter Grund vom Server wirkt – ein Netzausfall lässt die Wiederverbindung weiterlaufen.
 */
async function pruefeSitzungNach(): Promise<void> {
  if (sitzungspruefungLaeuft || zustand.ansicht !== 'spiel') return;
  sitzungspruefungLaeuft = true;
  try {
    const antwort = await holeIch(hol);
    if (!antwort.ok && antwort.grund !== null) melde({ art: 'sitzungFehlt', grund: antwort.grund });
  } finally {
    sitzungspruefungLaeuft = false;
  }
}

/**
 * „Abmelden“ im Menü: Sitzung beim Server beenden, dann zurück zur Anmeldeseite. Scheitert der
 * Aufruf (kein Netz, Server weg), lebt die Sitzung weiter – dann bleibt man im Spiel und bekommt
 * einen Hinweis, statt eine Anmeldeseite zu sehen, die einen sofort wieder durchlässt.
 */
async function meldeAb(zeigeHinweis: (text: string | null) => void): Promise<void> {
  zeigeHinweis(null);
  if (await sendeAbmelden(hol)) {
    melde({ art: 'abgemeldet' });
    return;
  }
  zeigeHinweis('Abmelden hat nicht geklappt. Versuch es noch einmal.');
}

/**
 * Ob die Welt dieses Clients zu der des Servers passt. Weicht die Kennung ab, lädt der Tab einmal
 * neu (vermutlich eine alte Fassung) und liefert false; hat er das für diese Kennung schon getan,
 * oder kann er es sich nicht merken, geht es mit einer Fehlermeldung in der Konsole weiter –
 * lieber das als ein Tab, der ohne Ende lädt (game/welt.ts, ADR 0027).
 */
function weltPasst(serverWorldId: string, gebauteWorldId: string): boolean {
  let neuGeladenFuer: string | null = null;
  try {
    neuGeladenFuer = window.sessionStorage.getItem(NEU_GELADEN_SCHLUESSEL);
  } catch {
    // Ohne Speicher kein Gedächtnis; unten wird dann nicht neu geladen.
  }
  const entscheidung = weltEntscheidung(serverWorldId, gebauteWorldId, neuGeladenFuer);
  if (entscheidung === 'weiter') {
    try {
      window.sessionStorage.removeItem(NEU_GELADEN_SCHLUESSEL);
    } catch {
      // Nichts zu vergessen, wenn es keinen Speicher gibt.
    }
    return true;
  }
  if (entscheidung === 'neu laden') {
    try {
      window.sessionStorage.setItem(NEU_GELADEN_SCHLUESSEL, serverWorldId);
      window.location.reload();
      return false;
    } catch {
      // Kann sich der Tab das Neuladen nicht merken, lädt er nicht – sonst womöglich ohne Ende.
    }
  }
  console.error(
    `Die Welt des Servers (${serverWorldId}) passt nicht zu der, die dieser Client baut (${gebauteWorldId}). ` +
      'Liegt eine alte Fassung im Tab, hilft Neuladen.',
  );
  return true;
}

// ---------------------------------------------------------------------------------------------
// Das Spiel
// ---------------------------------------------------------------------------------------------

/**
 * Baut die Verbindung auf und wartet auf das erste `hello`; erst damit entsteht die Welt. Vorher
 * zählt nur `kicked` – alles andere schickt der Server ohnehin erst nach der Begrüßung.
 */
function starteSpiel(ich: IchDaten): Spiel {
  let laufend: LaufendesSpiel | null = null;
  let angehalten = false;

  // Die Verbindung trägt die Sitzung über das Cookie; ohne gültige Sitzung weist der Server den
  // Handshake mit 401 ab.
  const clock: WorldClock = createWorldClock(worldClockUrl(window.location), {
    onMessage: (msg) => {
      if (laufend !== null) {
        laufend.nimm(msg);
        return;
      }
      if (msg.t === 'kicked') {
        melde({ art: 'hinausgeworfen' });
        return;
      }
      if (msg.t !== 'hello' || angehalten) return;
      const welt = generateWorld(msg.params);
      if (!weltPasst(msg.worldId, welt.worldId)) return;
      laufend = baueSpiel(ich, msg, welt, clock);
    },
    onGetrennt: () => void pruefeSitzungNach(),
  });
  window.addEventListener('pagehide', () => clock.dispose());

  return {
    halteAn() {
      angehalten = true;
      clock.dispose();
      laufend?.halteAn();
    },
  };
}

function baueSpiel(ich: IchDaten, hello: HelloMessage, welt: GameWorld, clock: WorldClock): LaufendesSpiel {
  const state = createGameState(welt);
  const eigeneId = hello.you.id;
  let eigeneParzelle = hello.you.parcelId;
  const parzellen = new Map(welt.parcels.map((parcel) => [parcel.id, parcel]));
  // Höfe, Interaktion und Positionsmeldung sind reine Logik in game/ (ADR 0027).
  const hoefe = createHofAnsicht();
  const interaktion = createInteraktion();
  const melder = createPositionsmelder();
  const sichtbarkeit = createSichtbarkeitsmelder();
  const kiste = createKiste();
  const antworten: ActionResultMessage[] = [];
  /** Stand der eigenen Hof-Uhr in Hof-Sekunden, wie ihn der Server zuletzt nannte (task-015). */
  let hofSekunden = hello.you.farmSeconds;

  // Der Generator baut jeden Baum mit allen Äpfeln. Was die Höfe gerade tragen, steht im hello; vor
  // dem ersten Meshing gesetzt, muss dafür kein Chunk ein zweites Mal gebaut werden (task-014).
  for (const farm of hello.farms) {
    const parcel = parzellen.get(farm.parcelId);
    if (parcel !== undefined) setzeAepfel(welt.world, parcel, farm.tree.apples);
  }

  canvas.hidden = false;
  const view = createScene(canvas, welt.world);
  const animalViews = createAnimalViews(view.scene);
  // Ein Schild je Parzelle; wer der Besitzer ist, sagt der Server im hello und danach über
  // `parcelOwner` (task-012).
  const schilder = createSchilder(view.scene, welt.parcels);
  const licht = createDaylightTargets(view);
  const input = buildInput(canvas);
  const fps = createFpsDisplay(document.body);
  const hud = createHud(document.body);
  // Die Kistenansicht zeigt, was game/kiste.ts weiß; schließen fängt die Maus wieder (task-014).
  const kistenansicht = createKistenansicht(document.body, {
    beiKlick: (from, index) => void klickeKiste(from, index),
    beiSchliessen: () => {
      kiste.schliesse();
      input.fangeMaus();
    },
  });

  // Der eigene Avatar: Er hängt als Ansicht am Spielzustand und ist nur in der Schulterkamera zu
  // sehen; in der Ego-Sicht stünde er im Auge. Die Arme dagegen hängen an der Kamera und sind
  // genau umgekehrt sichtbar (task-011).
  let farben: AvatarColors = ich.avatar;
  const eigeneFigur = createAvatarModel(farben);
  eigeneFigur.object.visible = false;
  view.scene.add(eigeneFigur.object);
  const arme = createArme(view.scene, view.camera, farben);
  const gang = createGang();
  let sicht: Sicht = 'ego';

  // Die Mitspieler (task-016): wer da ist und wo, als reine Logik; die Figuren sind Ansicht. Für die
  // eigene Physik sind sie keine Hindernisse – die Simulation bekommt sie gar nicht zu sehen.
  const mitspieler = createRemotePlayers(eigeneId);
  const blasen = createSprechblasen();
  const mitspielerAnsicht = createMitspielerAnsicht(view.scene);
  // Die eigene Sprechblase hängt an der eigenen Figur und ist mit ihr in der Schulterkamera zu sehen.
  const eigeneBlase = createSprechblase();
  eigeneFigur.object.add(eigeneBlase.object);
  const anwesenheitsliste = createAnwesenheitsliste(document.body);
  /** Der Test-Haken kann die Liste zeigen, weil Tab ohne Pointer Lock nicht ankommt. */
  let listeErzwungen = false;
  const satzmenue = createSatzmenue(document.body, {
    beiWahl: (satz) => {
      sage(satz);
      input.fangeMaus();
    },
    beiSchliessen: () => input.fangeMaus(),
  });

  /** Zeigt Farben sofort an, ohne sie schon am Konto zu haben (Vorschau im Spiegel). */
  const zeigeFarben = (neue: AvatarColors): void => {
    eigeneFigur.setFarben(neue);
    arme.setFarben(neue);
  };

  const spiegel = createSpiegel(document.body, {
    async speichere(neue) {
      const antwort = await sendeAvatar(hol, neue);
      if (antwort.ok) return null;
      // Ist die Sitzung weg, hilft kein zweiter Versuch: Der Automat schickt zur Anmeldeseite.
      if (antwort.grund !== null) melde({ art: 'sitzungFehlt', grund: antwort.grund });
      return antwort.fehler;
    },
    beiVorschau: (neue) => zeigeFarben(neue),
    beiZu(neue, gespeichert) {
      // Abbrechen nimmt die Vorschau zurück; gespeichert wird sie zur neuen Wahrheit.
      if (gespeichert) farben = neue;
      else zeigeFarben(farben);
      input.fangeMaus();
    },
  });

  const menu: Menu = createMenu(document.body, {
    beiWeiterSpielen: () => input.fangeMaus(),
    beiSpiegel: () => spiegel.oeffne(farben, false),
    beiAbmelden: () => void meldeAb((text) => menu.setHinweis(text)),
  });

  // Beim ersten Anmelden ist die Wahl vor dem Spiegel der Weg ins Spiel (tasks/task-011).
  if (!ich.avatarGewaehlt) spiegel.oeffne(farben, true);

  /** Zustände des vorigen Ticks für die Interpolation von Kamera und Tieren. */
  let previousPlayer = copyPlayerState(state.player);
  let previousAnimals = state.animals;
  /**
   * Die erste verfügbare Handlung im Blick, einmal je Tick aus dem Zustand berechnet: Sie ist der
   * Hinweis im HUD und zugleich die Taste, die `interact` auslöst (ADR 0009, ADR 0011 Punkt 10).
   * Seit task-013 mit Besitz – auf einem fremden Hof bleibt sie leer.
   */
  let handlung: Action | undefined;
  let accumulator = 0;
  let lastFrameMs: number | null = null;
  let laeuft = true;

  /** Umgebung der Interaktion: Parzellen, der Zustand der Höfe und der eigene Hof. */
  const kontext = (): InteractionContext => ({ parcels: welt.parcels, farms: hoefe.hoefe(), ownParcelId: eigeneParzelle });

  /** Setzt den Avatar dorthin, wo der Server ihn sieht – beim hello und wenn er eine Meldung zurücknimmt. */
  function setzePosition(pos: Vec3, yaw: number, flying: boolean): void {
    state.player.pos = { x: pos.x, y: pos.y, z: pos.z };
    state.player.vel = { x: 0, y: 0, z: 0 };
    state.player.yaw = yaw;
    state.player.flying = flying;
    // Kein Zwischenbild von der alten zur neuen Stelle.
    previousPlayer = copyPlayerState(state.player);
    melder.uebernimm(state.player, simZeitMs(state.tick));
  }

  /**
   * Setzt die Tor- und Apfelblöcke, die sich laut Server geändert haben, und zeichnet ihre Chunks neu
   * – jeden Chunk nur einmal, auch wenn Tor und Baum darin liegen.
   */
  function wendeHofAenderungenAn(): void {
    const chunks = new Map<string, { cx: number; cz: number }>();
    const merke = (liste: readonly { cx: number; cz: number }[]): void => {
      for (const chunk of liste) chunks.set(`${chunk.cx},${chunk.cz}`, chunk);
    };
    for (const aenderung of hoefe.nimmTorAenderungen()) {
      const parcel = parzellen.get(aenderung.parcelId);
      if (parcel === undefined || !setzeTor(welt.world, parcel, aenderung.open)) continue;
      merke(chunksUmBlock(welt.world, parcel.gate.x, parcel.gate.z));
    }
    for (const aenderung of hoefe.nimmBaumAenderungen()) {
      const parcel = parzellen.get(aenderung.parcelId);
      if (parcel === undefined || !setzeAepfel(welt.world, parcel, aenderung.apples)) continue;
      merke(chunksDerAepfel(welt.world, parcel));
    }
    if (chunks.size > 0) view.erneuereChunks([...chunks.values()]);
  }

  /** Höfe aus `farm` (nach einer Handlung) oder `farms` (der Takt, gebündelt seit task-016). */
  function nimmHoefe(liste: readonly FarmState[]): void {
    const jetzt = performance.now();
    for (const farm of liste) {
      hoefe.wendeAn(farm, jetzt, state.tick);
      // Der eigene Hof kommt fünfmal je Sekunde und trägt die Hof-Uhr frischer als `clock`.
      if (farm.parcelId === eigeneParzelle) hofSekunden = farm.farmSeconds;
    }
    wendeHofAenderungenAn();
  }

  /**
   * Meldet dem Server den Tab, wenn er es noch nicht weiß. `hidden` ist sonst `document.hidden`;
   * der Test-Haken gibt einen Wert vor, weil sich ein Tab aus einem Werkzeug nicht verstecken lässt.
   */
  function meldeSichtbarkeit(hidden: boolean = document.hidden): boolean {
    const nachricht = sichtbarkeit.faellig(hidden);
    if (nachricht === null || !clock.send(nachricht)) return false;
    sichtbarkeit.gesendet(nachricht);
    return true;
  }
  const beiSichtbarkeit = (): void => void meldeSichtbarkeit();
  document.addEventListener('visibilitychange', beiSichtbarkeit);

  /** Übernimmt, was ein `hello` mitbringt: beim Aufbau und nach jeder Wiederverbindung. */
  function uebernimmBegruessung(msg: HelloMessage): void {
    const jetzt = performance.now();
    eigeneParzelle = msg.you.parcelId;
    hofSekunden = msg.you.farmSeconds;
    // Der Server hält einen Frischverbundenen für anwesend; liegt der Tab im Hintergrund, erfährt er es jetzt.
    sichtbarkeit.nachBegruessung();
    meldeSichtbarkeit();
    schilder.setzeAlle(msg.parcels);
    // Wer da ist, sagt die Begrüßung neu; Sätze aus der Zeit davor gelten nicht mehr (task-016).
    mitspieler.begruessung(msg.players, jetzt);
    blasen.vergissAlle();
    for (const farm of msg.farms) hoefe.wendeAn(farm, jetzt, state.tick);
    wendeHofAenderungenAn();
    state.inventory.stacks = kopiereStapel(msg.inventory);
    // Die neue Verbindung hat die Kiste noch nicht geöffnet – auch beim Server ist sie zu.
    kiste.vergiss();
    kiste.setzeFaecher(msg.chest);
    // Der Server misst jede Interaktion an der Position, die er kennt; der Client fängt dort an.
    setzePosition(msg.you.pos, msg.you.yaw, msg.you.flying);
    state.animals = hoefe.tiere(jetzt, state.tick);
    previousAnimals = state.animals;
    // Eine Antwort auf eine Anfrage der alten Verbindung kommt nicht mehr.
    interaktion.vergiss();
    handlung = handlungImBlick(state, kontext());
  }

  /**
   * Die Nachrichten des Servers. Der Verteiler kennt sie alle, damit keine still im Nichts
   * verschwindet (client/src/net/nachrichten.ts); die noch leeren Handler nennen die Task, die
   * sie füllt.
   */
  const nachrichten: NachrichtenHandler = {
    hello: (msg) => {
      // Nach einer Wiederverbindung kann der Server eine andere Welt haben (neu gestartet).
      if (msg.worldId !== welt.worldId && !weltPasst(msg.worldId, welt.worldId)) return;
      uebernimmBegruessung(msg);
    },
    clock: (msg) => {
      hofSekunden = msg.farmSeconds;
    },
    players: (msg) => {
      // Der eigene Eintrag steht nur darin, wenn der Server eine Positionsmeldung für unmöglich hielt:
      // dann gilt seine Position.
      const pose = ruecksetzungFuer(msg, eigeneId);
      if (pose !== null) setzePosition(pose.pos, pose.yaw, pose.flying);
      // Alle anderen Einträge bewegen die Mitspieler (task-016).
      mitspieler.posen(msg.list, performance.now());
    },
    playerJoined: (msg) => mitspieler.kommt(msg.player, performance.now()),
    playerLeft: (msg) => {
      mitspieler.geht(msg.id);
      blasen.vergiss(msg.id);
    },
    farm: (msg) => nimmHoefe([msg.farm]),
    farms: (msg) => nimmHoefe(msg.list),
    inventory: (msg) => {
      state.inventory.stacks = kopiereStapel(msg.stacks);
    },
    chest: (msg) => kiste.setzeFaecher(msg.slots),
    actionResult: (msg) => {
      antworten.push(msg);
      if (antworten.length > TEST_ANTWORTEN) antworten.shift();
      // Wartet die Kiste auf eine Verschiebung, ist es ihre Antwort; der Satz steht dann in der Ansicht.
      const fuerKiste = kiste.antwort(msg, performance.now());
      if (fuerKiste?.art === 'verschiebung') {
        if (fuerKiste.meldung !== null) kistenansicht.zeigeMeldung(fuerKiste.meldung);
        return;
      }
      const text = interaktion.antwort(msg);
      if (text !== null) hud.zeigeMeldung(text);
      if (fuerKiste?.art === 'oeffnung' && fuerKiste.auf) gibMausFuerKiste();
    },
    // Ein fester Satz, auch der eigene: Der Server schickt ihn allen in der Nähe, dem Sprecher ebenso (task-016).
    say: (msg) => blasen.sage(msg.id, msg.text, performance.now()),
    parcelOwner: (msg) => schilder.setBesitzer(msg.id, msg.owner),
    kicked: () => melde({ art: 'hinausgeworfen' }),
  };

  uebernimmBegruessung(hello);

  /** Feste Blöcke der Welt; die Schulterkamera rückt davor, statt in einer Wand zu stecken. */
  const istFest = (x: number, y: number, z: number): boolean => isSolid(welt.world, x, y, z);

  /** Nur im Devserver: der Test-Haken darf die Absicht des nächsten Ticks überlagern (ADR 0015). */
  const testHaken = import.meta.env.DEV
    ? installiereTestHaken({
        state,
        welt,
        clock,
        ich,
        tick: simulateTick,
        actions: () => {
          const erste = handlungImBlick(state, kontext());
          return erste === undefined ? [] : [erste];
        },
        hoefe: () => hoefe.hoefe(),
        antworten: () => [...antworten],
        parzelle: () => eigeneParzelle,
        hofSekunden: () => hofSekunden,
        meldeTab: (hidden) => meldeSichtbarkeit(hidden),
        kiste: () => ({ offen: kiste.istOffen(), faecher: kiste.faecher().map((fach) => (fach === null ? null : { ...fach })) }),
        klickeKiste: (from, index) => klickeKiste(from, index),
        schliesseKiste: () => kiste.schliesse(),
        sicht: () => sicht,
        wechsleSicht: () => {
          sicht = sicht === 'ego' ? 'schulter' : 'ego';
        },
        farben: () => farben,
        oeffneSpiegel: () => spiegel.oeffne(farben, false),
        grafik: () => ({
          drawCalls: view.renderer.info.render.calls,
          triangles: view.renderer.info.render.triangles,
          chunks: view.chunkCount,
        }),
        zeichneProbe: (bilder) => {
          const beginn = performance.now();
          for (let i = 0; i < bilder; i++) view.render();
          return (performance.now() - beginn) / Math.max(1, bilder);
        },
        mitspieler: () => mitspieler.staende(performance.now()),
        anwesende: () => anwesenheitsZeilen(ich.name, mitspieler.liste()),
        zeigeListe: (an) => {
          listeErzwungen = an;
        },
        oeffneSatzMenue,
        satzMenue: () => satzmenue.istOffen(),
        waehleSatz: (satz) => {
          if (!satzmenue.istOffen()) return false;
          satzmenue.schliesse();
          return sage(satz);
        },
        sprechblasen: () => {
          const jetztMs = performance.now();
          return [eigeneId, ...mitspieler.liste().map((m) => m.id)].flatMap((id) => {
            const text = blasen.text(id, jetztMs);
            return text === null ? [] : [{ id, text }];
          });
        },
      })
    : null;

  /**
   * Meldet die eigene Position, wenn sie fällig ist; `sofort` vor einer Interaktion. Gerechnet wird in
   * der Zeit der Simulation: genau jeden dritten Tick, damit die anderen einen gleichmäßigen Gang sehen
   * (task-016, ADR 0032).
   */
  function meldePosition(sofort: boolean): void {
    const jetzt = simZeitMs(state.tick);
    const bewegung = melder.faellig(state.player, jetzt, sofort);
    if (bewegung !== null && clock.send(bewegung)) melder.gesendet(bewegung, jetzt);
  }

  /** Die Taste der Handlung wurde gedrückt: Anfrage an den Server, nicht ausführen. */
  function loeseHandlung(jetzt: number): void {
    const nachricht = interaktion.loese(handlung, jetzt);
    if (nachricht === null) return;
    // Erst die Position, dann die Handlung: Der Server misst die Nähe an dem, was er zuletzt hörte.
    meldePosition(true);
    if (clock.send(nachricht)) {
      // Die eigene Kiste geht auf, sobald der Server zustimmt (task-014).
      if (nachricht.target.kind === 'chest') kiste.oeffnetGleich(jetzt);
      return;
    }
    interaktion.vergiss();
    hud.zeigeMeldung(OHNE_VERBINDUNG);
  }

  /** Ein Klick auf ein Fach der Kistenansicht: als `chestMove` an den Server, wenn es etwas umzulegen gibt. */
  function klickeKiste(from: ChestSide, index: number): boolean {
    const nachricht = kiste.klick(from, index, state.inventory.stacks, performance.now());
    if (nachricht === null) return false;
    if (clock.send(nachricht)) return true;
    kistenansicht.zeigeMeldung(OHNE_VERBINDUNG);
    return false;
  }

  /**
   * Ein Satz aus dem Menü geht als Nummer an den Server (task-016). Die Blase – auch die eigene – kommt
   * mit seiner Antwort; so steht über dem eigenen Kopf, was die anderen sehen.
   */
  function sage(satz: PhraseIndex): boolean {
    if (clock.send({ v: PROTOCOL_VERSION, t: 'say', phrase: satz })) return true;
    hud.zeigeMeldung(OHNE_VERBINDUNG);
    return false;
  }

  /** Taste T: das Satz-Menü; die Maus wird frei, damit man auch klicken kann. */
  function oeffneSatzMenue(): void {
    satzmenue.oeffne();
    if (document.pointerLockElement === canvas) document.exitPointerLock();
  }

  /** Die Kiste ist offen: Die Maus wird frei, damit man in die Fächer klicken kann. */
  function gibMausFuerKiste(): void {
    if (document.pointerLockElement === canvas) document.exitPointerLock();
  }

  /** Genau ein Simulationsschritt: Zustände sichern, Absicht holen, rechnen, Handlung neu bestimmen. */
  function simulateTick(): void {
    const jetzt = performance.now();
    previousPlayer = copyPlayerState(state.player);
    previousAnimals = state.animals;
    // Die Tiere kommen aus den Hof-Nachrichten, je Tick frisch zwischen den letzten zweien gelesen.
    // Der Avatar hält weiter an ihnen an (ADR 0018); bewegt werden sie nur auf dem Server.
    state.animals = hoefe.tiere(jetzt, state.tick);
    let absicht = input.nextIntent(handlung?.key);
    if (testHaken !== null) absicht = testHaken.ueberlagere(absicht);
    if (absicht.interact) loeseHandlung(jetzt);
    stepSimulation(state, { ...absicht, interact: false }, welt.world, welt.pens, { animals: false });
    handlung = handlungImBlick(state, kontext());
    meldePosition(false);
  }

  function frame(nowMs: number): void {
    if (!laeuft) return;
    const dtSekunden = lastFrameMs === null ? 0 : (nowMs - lastFrameMs) / 1000;
    accumulator += dtSekunden;
    lastFrameMs = nowMs;

    let ticks = 0;
    while (accumulator >= TICK_SECONDS && ticks < MAX_TICKS_PER_FRAME) {
      simulateTick();
      accumulator -= TICK_SECONDS;
      ticks++;
    }
    // Was über die nachgeholten Ticks hinausgeht, wird verworfen, statt später aufzuholen.
    if (accumulator >= TICK_SECONDS) accumulator = 0;

    const alpha = accumulator / TICK_SECONDS;
    // Taste V: Ego-Sicht oder Schulterkamera. Das ist reine Ansicht und ändert am Zustand nichts.
    if (input.nimmSichtWechsel()) sicht = sicht === 'ego' ? 'schulter' : 'ego';
    // Taste T: das Satz-Menü – nicht, während Kiste oder Spiegel offen sind (task-016).
    if (input.nimmSatzMenue() && !kiste.istOffen() && !spiegel.istOffen()) oeffneSatzMenue();

    const auge = interpolatePose(previousPlayer, state.player, alpha);
    applyPose(view.camera, sicht === 'schulter' ? schulterPose(auge, istFest) : auge);

    // Der Gang kommt aus der gezeichneten Strecke, nicht aus der Bildrate; Arme und Beine der
    // Figur und die eigenen Arme im Bild pendeln darum im selben Takt (ADR 0014).
    const swing = gang.schritt(auge.x, auge.z, dtSekunden);
    eigeneFigur.setSwing(swing);
    eigeneFigur.object.visible = sicht === 'schulter';
    eigeneFigur.object.position.set(auge.x, auge.y - PLAYER.eyeHeight, auge.z);
    eigeneFigur.object.rotation.y = auge.yaw;
    arme.setSwing(swing);
    arme.setSichtbar(sicht === 'ego');

    animalViews.sync(state.animals, previousAnimals, alpha, state.tick, dtSekunden);
    schilder.sync({ x: auge.x, z: auge.z });
    // Die Mitspieler zwischen den letzten players-Nachrichten, dazu die Sätze – auch der eigene (task-016).
    const jetztMs = performance.now();
    mitspielerAnsicht.sync(mitspieler.staende(jetztMs), (id) => blasen.text(id, jetztMs), dtSekunden, { x: auge.x, z: auge.z });
    const eigenerSatz = blasen.text(eigeneId, jetztMs);
    eigeneBlase.setText(eigenerSatz);
    const tagesanteil = dayFraction(clock.serverNow());
    applyDaylight(licht, tagesanteil);
    view.render();

    hud.setZeit(tagesanteil, hofSekunden);
    hud.setHint(handlung ?? null);
    hud.setInventory(state.inventory);
    // In der Ego-Sicht ist die eigene Blase nicht zu sehen; dort steht der Satz über der Leiste.
    hud.setEigenerSatz(sicht === 'ego' ? eigenerSatz : null);
    const anwesende = anwesenheitsZeilen(ich.name, mitspieler.liste());
    anwesenheitsliste.zeige(input.listeGehalten() || listeErzwungen, anwesende);
    menu.setAnwesende(anwesende);
    kistenansicht.zeige({ offen: kiste.istOffen(), faecher: kiste.faecher(), stacks: state.inventory.stacks });
    // Vor dem ersten geglückten Verbindungsaufbau bleibt die Anzeige unsichtbar (ADR 0012, Punkt 6).
    if (clock.status() !== 'connecting') hud.setConnection(clock.connected());
    fps.frame(nowMs);
    // Spiegel und Kiste liegen über dem Menü; beides gleichzeitig wäre nur Gedränge.
    menu.setVisible(!input.isLocked() && !spiegel.istOffen() && !kiste.istOffen() && !satzmenue.istOffen());
    requestAnimationFrame(frame);
  }

  requestAnimationFrame(frame);

  return {
    nimm: (msg) => verarbeiteNachricht(msg, nachrichten),
    halteAn() {
      laeuft = false;
      document.removeEventListener('visibilitychange', beiSichtbarkeit);
      menu.setVisible(false);
      spiegel.dispose();
      kistenansicht.dispose();
      satzmenue.dispose();
      anwesenheitsliste.dispose();
      mitspielerAnsicht.dispose();
      arme.dispose();
      eigeneBlase.dispose();
      eigeneFigur.dispose();
      schilder.dispose();
      if (document.pointerLockElement === canvas) document.exitPointerLock();
    },
  };
}

/** Eigenständige Kopien der Stapel; der Spielzustand teilt nichts mit einer Nachricht. */
function kopiereStapel(stacks: readonly ItemStack[]): ItemStack[] {
  return stacks.map((stapel) => ({ item: stapel.item, count: stapel.count }));
}

function holeCanvas(): HTMLCanvasElement {
  const element = document.getElementById('spiel');
  if (!(element instanceof HTMLCanvasElement)) {
    throw new Error('Das Canvas #spiel fehlt in index.html.');
  }
  return element;
}

// ---------------------------------------------------------------------------------------------
// Test-Haken für die Abnahme ohne Pointer Lock (nur im Devserver, `import.meta.env.DEV`).
// Vite ersetzt die Bedingung im Produktions-Build durch `false`; dort gibt es kein `window.__bauernhof`.
// Beschrieben in README.md, Abschnitt „Test-Haken“, begründet in ADR 0015.
// ---------------------------------------------------------------------------------------------

/** Was `window.__bauernhof` im Devserver bereitstellt. */
export interface BauernhofTestHook {
  /** Der lebende Spielzustand; darf für Tests direkt verändert werden (Blick, nicht aber Sprünge – s. u.). */
  state: GameState;
  world: World;
  /** Die erzeugte Welt: Dorf, Wege, Parzellen, Koppeln, Tiere und Landmarken. */
  welt: GameWorld;
  clock: WorldClock;
  /** Das angemeldete Konto aus `GET /api/ich`; ohne Anmeldung gibt es den Haken nicht. */
  ich: IchDaten;
  /**
   * Überlagert die Absicht der folgenden Ticks. Gehaltene Felder (`forward`, `strafe`, `jump`,
   * `up`, `down`) gelten bis zum nächsten Aufruf; Flanken und Deltas (`toggleFly`, `interact`,
   * `lookYaw`, `lookPitch`) wirken genau einen Tick. `setIntent({})` hebt alles auf.
   */
  setIntent(partial: Partial<Intent>): void;
  /** Führt sofort genau einen Simulationsschritt aus, unabhängig von der Bildrate. */
  tick(): void;
  /** Die Handlung, die gerade im Blick ist – mit Besitz, also auf fremden Höfen leer. */
  actions(): Action[];
  /** Die Höfe, wie sie zuletzt vom Server kamen. */
  hoefe(): FarmState[];
  /** Die letzten Antworten des Servers auf `interact`, älteste zuerst. */
  antworten(): ActionResultMessage[];
  /** Die eigene Parzelle aus dem hello, oder null. */
  parzelle(): number | null;
  /** Stand der eigenen Hof-Uhr in Hof-Sekunden, wie ihn der Server zuletzt nannte; daraus der Hoftag. */
  hofSekunden(): number;
  /**
   * Meldet dem Server einen versteckten (`true`) oder sichtbaren Tab, als hätte der Browser
   * `visibilitychange` ausgelöst – ein Tab lässt sich aus einem Werkzeug nicht verstecken. Liefert,
   * ob eine Meldung hinausging (nur, wenn der Server es noch nicht wusste).
   */
  meldeTab(hidden: boolean): boolean;
  /** Die eigene Kiste, wie sie der Client gerade zeigt: offen oder zu, und die sechzehn Fächer (task-014). */
  kiste(): { offen: boolean; faecher: Kistenfach[] };
  /**
   * Klickt auf ein Fach der Kistenansicht (`'kiste'` 0–15 oder `'leiste'` 0–7), als hätte man es mit
   * der Maus getan. Liefert, ob ein `chestMove` hinausging – nicht, wenn die Kiste zu ist, das Fach
   * leer ist oder die vorige Antwort noch aussteht.
   */
  klickeKiste(from: ChestSide, index: number): boolean;
  /** Schließt die Kistenansicht wie Escape, ohne die Maus zu fangen. */
  schliesseKiste(): void;
  /**
   * Läuft zu Fuß zum Punkt (x, z), so wie ein Mensch mit W und Maus – der Server nimmt einen
   * Sprung über den Hof zurück, darum hilft `state.player.pos = …` nicht mehr. Liefert true bei
   * Ankunft, false nach `maxMs` (etwa vor einem Zaun). Braucht laufende Ticks (Bilder oder `tick()`).
   */
  geheZu(x: number, z: number, maxMs?: number): Promise<boolean>;
  /** Richtet den Blick vom Auge auf den Punkt (x, y, z). */
  blickeAuf(x: number, y: number, z: number): void;
  /** Welche Kamera gerade zeigt. */
  sicht(): Sicht;
  /** Wechselt zwischen Ego-Sicht und Schulterkamera – wie die Taste V, aber ohne Pointer Lock. */
  wechsleSicht(): void;
  /** Die Avatarfarben, die gerade am Konto stehen. */
  farben(): AvatarColors;
  /** Öffnet den Spiegel, als käme man über das Menü. */
  oeffneSpiegel(): void;
  /** Zeichenstatistik des zuletzt gezeichneten Bildes, für die Sichtprüfung der Bildrate. */
  grafik(): { drawCalls: number; triangles: number; chunks: number };
  /**
   * Zeichnet `bilder` Bilder hintereinander und liefert die mittlere Zeit je Bild in
   * Millisekunden. Für die Sichtprüfung der Bildrate in Umgebungen, in denen
   * `requestAnimationFrame` gedrosselt wird (eingebettete Browser, verdeckte Fenster).
   */
  zeichneProbe(bilder: number): number;
  /** Die Mitspieler, wie sie gerade gezeichnet werden: Name, Farben, Ort und Blick (task-016). */
  mitspieler(): MitspielerStand[];
  /** Die Anwesenheitsliste, wie Tab sie zeigt: man selbst zuerst, dann die anderen nach Namen. */
  anwesende(): AnwesenheitsZeile[];
  /** Zeigt die Anwesenheitsliste wie gehaltenes Tab (`true`) oder nimmt sie weg – ohne Pointer Lock kommt Tab nicht an. */
  zeigeListe(an: boolean): void;
  /** Öffnet das Satz-Menü wie die Taste T. */
  oeffneSatzMenue(): void;
  /** Ob das Satz-Menü offen ist. */
  satzMenue(): boolean;
  /** Wählt im offenen Satz-Menü einen Satz (0–5) wie mit Ziffer oder Klick; liefert, ob er hinausging. */
  waehleSatz(satz: PhraseIndex): boolean;
  /** Die Sätze, die gerade als Blase stehen, auch der eigene. */
  sprechblasen(): { id: number; text: string }[];
}

/** Was der Test-Haken aus dem laufenden Spiel bekommt. */
interface TestHakenTeile {
  state: GameState;
  welt: GameWorld;
  clock: WorldClock;
  ich: IchDaten;
  tick(): void;
  actions(): Action[];
  hoefe(): FarmState[];
  antworten(): ActionResultMessage[];
  parzelle(): number | null;
  hofSekunden(): number;
  meldeTab(hidden: boolean): boolean;
  kiste(): { offen: boolean; faecher: Kistenfach[] };
  klickeKiste(from: ChestSide, index: number): boolean;
  schliesseKiste(): void;
  sicht(): Sicht;
  wechsleSicht(): void;
  farben(): AvatarColors;
  oeffneSpiegel(): void;
  grafik(): { drawCalls: number; triangles: number; chunks: number };
  zeichneProbe(bilder: number): number;
  mitspieler(): MitspielerStand[];
  anwesende(): AnwesenheitsZeile[];
  zeigeListe(an: boolean): void;
  oeffneSatzMenue(): void;
  satzMenue(): boolean;
  waehleSatz(satz: PhraseIndex): boolean;
  sprechblasen(): { id: number; text: string }[];
}

declare global {
  interface Window {
    __bauernhof?: BauernhofTestHook;
  }
}

/** Felder der Absicht, die als Flanke bzw. Delta nur einen Tick lang wirken. */
const FLANKEN_FELDER: ReadonlySet<keyof Intent> = new Set<keyof Intent>(['toggleFly', 'interact', 'lookYaw', 'lookPitch']);
/** Ab diesem Abstand gilt `geheZu` als angekommen; ein Schritt je Tick ist rund 0,14 Blöcke. */
const ANGEKOMMEN = 0.25;

function installiereTestHaken(teile: TestHakenTeile): { ueberlagere(absicht: Intent): Intent } {
  const { state } = teile;
  let dauerhaft: Partial<Intent> = {};
  let einmalig: Partial<Intent> = {};
  let ziel: { x: number; z: number; bis: number; fertig(angekommen: boolean): void } | null = null;

  window.__bauernhof = {
    state,
    world: teile.welt.world,
    welt: teile.welt,
    clock: teile.clock,
    ich: teile.ich,
    setIntent(partial) {
      dauerhaft = {};
      einmalig = {};
      for (const [feld, wert] of Object.entries(partial) as [keyof Intent, Intent[keyof Intent] | undefined][]) {
        if (wert === undefined) continue;
        const ablage: Record<string, unknown> = FLANKEN_FELDER.has(feld) ? einmalig : dauerhaft;
        ablage[feld] = wert;
      }
    },
    tick: teile.tick,
    actions: teile.actions,
    hoefe: teile.hoefe,
    antworten: teile.antworten,
    parzelle: teile.parzelle,
    hofSekunden: teile.hofSekunden,
    meldeTab: teile.meldeTab,
    kiste: teile.kiste,
    klickeKiste: teile.klickeKiste,
    schliesseKiste: teile.schliesseKiste,
    geheZu(x, z, maxMs = 15_000) {
      ziel?.fertig(false);
      return new Promise<boolean>((fertig) => {
        ziel = { x, z, bis: performance.now() + maxMs, fertig };
      });
    },
    blickeAuf(x, y, z) {
      const dx = x - state.player.pos.x;
      const dy = y - (state.player.pos.y + PLAYER.eyeHeight);
      const dz = z - state.player.pos.z;
      state.player.yaw = Math.atan2(-dx, -dz);
      state.player.pitch = Math.atan2(dy, Math.hypot(dx, dz));
    },
    sicht: teile.sicht,
    wechsleSicht: teile.wechsleSicht,
    farben: teile.farben,
    oeffneSpiegel: teile.oeffneSpiegel,
    grafik: teile.grafik,
    zeichneProbe: teile.zeichneProbe,
    mitspieler: teile.mitspieler,
    anwesende: teile.anwesende,
    zeigeListe: teile.zeigeListe,
    oeffneSatzMenue: teile.oeffneSatzMenue,
    satzMenue: teile.satzMenue,
    waehleSatz: teile.waehleSatz,
    sprechblasen: teile.sprechblasen,
  };

  return {
    ueberlagere(absicht) {
      let ergebnis: Intent = { ...absicht, ...dauerhaft, ...einmalig };
      einmalig = {};
      if (ziel !== null) {
        const dx = ziel.x - state.player.pos.x;
        const dz = ziel.z - state.player.pos.z;
        const rest = Math.hypot(dx, dz);
        if (rest < ANGEKOMMEN || performance.now() > ziel.bis) {
          const erledigt = ziel;
          ziel = null;
          erledigt.fertig(rest < ANGEKOMMEN);
        } else {
          // Zum Ziel drehen (kürzester Winkel) und geradeaus gehen.
          const soll = Math.atan2(-dx, -dz);
          const drehung = soll - state.player.yaw - 2 * Math.PI * Math.round((soll - state.player.yaw) / (2 * Math.PI));
          ergebnis = { ...ergebnis, lookYaw: drehung, forward: 1, strafe: 0 };
        }
      }
      return ergebnis;
    },
  };
}
